import { useEffect, useState } from "react";
import {
  ROOM_TAB_CHANNEL_PREFIX,
  ROOM_TAB_CLAIM_TIMEOUT_MS,
  ROOM_TAB_MESSAGE,
} from "../zoom-constant";

interface RoomTabMessage {
  type: (typeof ROOM_TAB_MESSAGE)[keyof typeof ROOM_TAB_MESSAGE];
  from: string;
}

const newTabId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;

/**
 * Whether this room is already open in another tab of the same browser.
 *
 * The tab pings a per-room `BroadcastChannel` on mount. Only a tab that has
 * claimed the room answers, so an answer means the room is held elsewhere;
 * silence for `ROOM_TAB_CLAIM_TIMEOUT_MS` means this tab claims it. Two tabs
 * opened in the same instant both stay silent and both claim — the session then
 * behaves as it did before, which is the safer way to be wrong.
 */
export const useDuplicateRoomTab = (roomId?: string): boolean => {
  const [isDuplicateTab, setIsDuplicateTab] = useState(false);

  useEffect(() => {
    if (!roomId || typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel(`${ROOM_TAB_CHANNEL_PREFIX}${roomId}`);
    const tabId = newTabId();
    let hasClaimedRoom = false;

    const claimTimer = setTimeout(() => {
      hasClaimedRoom = true;
    }, ROOM_TAB_CLAIM_TIMEOUT_MS);

    channel.onmessage = ({ data }: MessageEvent<RoomTabMessage>) => {
      if (!data || data.from === tabId) return;

      if (data.type === ROOM_TAB_MESSAGE.PING && hasClaimedRoom) {
        channel.postMessage({ type: ROOM_TAB_MESSAGE.PONG, from: tabId });
        return;
      }

      if (data.type === ROOM_TAB_MESSAGE.PONG) {
        /* Clearing the timer keeps this tab from also claiming the room. */
        clearTimeout(claimTimer);
        setIsDuplicateTab(true);
      }
    };

    channel.postMessage({ type: ROOM_TAB_MESSAGE.PING, from: tabId });

    return () => {
      clearTimeout(claimTimer);
      channel.close();
    };
  }, [roomId]);

  return isDuplicateTab;
};
